/**
 * 动效预设注册表（8.2 四类动效预设）。
 *
 * 数据单一来源：shared/motion-recipes.json —— 前端从这里 import，后端种子脚本
 * seed_motion_recipes.py 从这里读取灌入 motion_recipes 表，避免两份数据漂移。
 *
 * 命名与 6.4 属性面板 motionPreset 选项（none/fade-in/slide-up/scale-in）对齐：
 * MOTION_PRESET_OPTIONS 即面板下拉值，fade-in/slide-up/scale-in 同时是 entrance 配方名。
 */
import motionRecipesJson from "@shared/motion-recipes.json";

import type {
  MotionCategory,
  MotionPreset,
  MotionRecipe,
  MotionTrigger,
  RecipeConfig,
} from "./types";

/** 四类动效预设（8.2 表）。 */
export const MOTION_CATEGORIES: readonly MotionCategory[] = [
  "entrance",
  "emphasis",
  "attention",
  "exit",
];

/** 6.4 属性面板 motionPreset 下拉选项（与设计文档逐项一致）。 */
export const MOTION_PRESET_OPTIONS = ["none", "fade-in", "slide-up", "scale-in"] as const;

export type MotionPresetOption = (typeof MOTION_PRESET_OPTIONS)[number];

type PresetId = Exclude<MotionPresetOption, "none">;

const PRESET_LABELS: Record<PresetId, string> = {
  "fade-in": "淡入",
  "slide-up": "上滑进入",
  "scale-in": "缩放进入",
};

const PRESET_DESCRIPTIONS: Record<PresetId, string> = {
  "fade-in": "透明度由 0 渐变到 1",
  "slide-up": "从下方 16px 上滑并淡入",
  "scale-in": "从 0.95 放大到 1 并淡入",
};

/**
 * 校验一个配方参数是否合法。
 * 返回错误信息数组；空数组表示合法。供 presets.test 与加载期校验复用。
 */
export function validateMotionRecipe(recipe: MotionRecipe): string[] {
  const errors: string[] = [];
  const label = recipe.name || "<unnamed>";

  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(recipe.name)) {
    errors.push(`${label}: name 必须是小写 kebab-case`);
  }
  if (!MOTION_CATEGORIES.includes(recipe.category)) {
    errors.push(`${label}: 未知 category "${recipe.category}"`);
  }
  if (!Array.isArray(recipe.intentKeywords) || recipe.intentKeywords.length === 0) {
    errors.push(`${label}: intentKeywords 不能为空`);
  }
  if (recipe.recipe.kind !== "keyframes" && recipe.recipe.kind !== "transition") {
    errors.push(`${label}: recipe.kind 必须是 keyframes 或 transition`);
  }
  if (!(recipe.recipe.duration > 0)) {
    errors.push(`${label}: duration 必须 > 0，当前 ${recipe.recipe.duration}`);
  }
  if (!recipe.recipe.easing) {
    errors.push(`${label}: easing 不能为空`);
  }
  if (recipe.recipe.delay < 0) {
    errors.push(`${label}: delay 不能 < 0`);
  }
  if (recipe.recipe.kind === "keyframes") {
    const frames = recipe.recipe.keyframes;
    if (!frames || Object.keys(frames).length === 0) {
      errors.push(`${label}: keyframes 配方缺少 keyframes 步骤表`);
    }
  } else if (!recipe.recipe.transition) {
    errors.push(`${label}: transition 配方缺少 transition 参数`);
  }
  if (recipe.restraint.maxDuration !== undefined && !(recipe.restraint.maxDuration > 0)) {
    errors.push(`${label}: restraint.maxDuration 必须 > 0`);
  }
  if (recipe.restraint.maxElements !== undefined && recipe.restraint.maxElements < 1) {
    errors.push(`${label}: restraint.maxElements 必须 >= 1`);
  }
  if (!Array.isArray(recipe.exportFormats) || recipe.exportFormats.length === 0) {
    errors.push(`${label}: exportFormats 不能为空`);
  }
  return errors;
}

function normalizeRecipe(raw: unknown): MotionRecipe {
  const recipe = raw as MotionRecipe;
  const errors = validateMotionRecipe(recipe);
  if (errors.length > 0) {
    throw new Error(`shared/motion-recipes.json 含非法配方：\n- ${errors.join("\n- ")}`);
  }
  return { ...recipe, isBuiltin: true };
}

/** 全部内置配方（按种子数据顺序，分类内保持文档顺序）。 */
export const MOTION_RECIPES: MotionRecipe[] = (motionRecipesJson as unknown[]).map(normalizeRecipe);

/** 按类别分组：RECIPES_BY_CATEGORY.entrance / emphasis / attention / exit。 */
export const RECIPES_BY_CATEGORY: Record<MotionCategory, MotionRecipe[]> = MOTION_CATEGORIES.reduce(
  (acc, category) => {
    acc[category] = MOTION_RECIPES.filter((r) => r.category === category);
    return acc;
  },
  {} as Record<MotionCategory, MotionRecipe[]>,
);

/** 按配方名查配方。 */
export function getRecipeByName(name: string): MotionRecipe | undefined {
  return MOTION_RECIPES.find((r) => r.name === name);
}

/** 按触发时机查配方。 */
export function getRecipesByTrigger(trigger: MotionTrigger): MotionRecipe[] {
  return MOTION_RECIPES.filter((r) => r.recipe.trigger === trigger);
}

/**
 * 属性面板预设：仅包含面板下拉中的动效选项（fade-in/slide-up/scale-in），
 * 与 6.4 的 motionPreset 枚举一一对应；"none" 表示不运行动效，不在预设表中。
 */
export const MOTION_PRESETS: MotionPreset[] = MOTION_PRESET_OPTIONS.filter(
  (id): id is PresetId => id !== "none",
).map((id) => {
  const recipe = getRecipeByName(id);
  if (!recipe) {
    throw new Error(`MOTION_PRESETS: 找不到与面板选项 "${id}" 对应的配方`);
  }
  return {
    id,
    label: PRESET_LABELS[id],
    description: PRESET_DESCRIPTIONS[id],
    category: recipe.category,
    recipe,
  };
});

/** 获取面板预设；"none" 或无匹配返回 undefined（调用方应视作不运行动效）。 */
export function getPreset(id: MotionPresetOption | string): MotionPreset | undefined {
  if (id === "none") return undefined;
  return MOTION_PRESETS.find((p) => p.id === id);
}

/** 供 debug / 测试使用：打印配方明细。 */
export function describeRecipe(recipe: MotionRecipe): string {
  const cfg: RecipeConfig = recipe.recipe;
  return [
    `${recipe.name} [${recipe.category}]`,
    `  trigger=${cfg.trigger} duration=${cfg.duration}ms easing=${cfg.easing} delay=${cfg.delay}ms`,
    `  keywords=${recipe.intentKeywords.join(", ")}`,
    `  restraint=${JSON.stringify(recipe.restraint)}`,
  ].join("\n");
}
